import installPointToTarget   from "../engine/engine_parts/targeting/PointToTarget";
import installOriginDirection from "../engine/engine_parts/targeting/OriginDirection";
import installTokenToTarget   from "../engine/engine_parts/targeting/TokenToTarget";
import installGlobalAmbient   from "../engine/engine_parts/targeting/GlobalAmbient";

const UI_TARGETING_PRESET_KEY = "EmitterTool:UI:targetingPresetV1";

export default function installTargetingManager({ host, getOrigin }) {
  if (!host) throw new Error("TargetingManager: host is required");
  if (typeof getOrigin !== "function") getOrigin = () => ({ x: 0, y: 0 });

  const prevPos = host.style.position || "";
  const cs = getComputedStyle(host);
  if (cs.position === "static") host.style.position = "relative";

  const cvs = document.createElement("canvas");
  cvs.style.position = "absolute";
  cvs.style.inset = "0";
  cvs.style.pointerEvents = "none";
  cvs.style.zIndex = "9999";
  cvs.style.background = "transparent";
  cvs.style.backgroundColor = "transparent";
  cvs.style.width = "100%";
  cvs.style.height = "100%";
  host.appendChild(cvs);
  const ctx = cvs.getContext("2d", { alpha: true });

  let mo;
  try {
    mo = new MutationObserver(() => {
      if (host.lastElementChild !== cvs) host.appendChild(cvs);
    });
    mo.observe(host, { childList: true });
  } catch {}

  function placeOverlay() {
    const r = host.getBoundingClientRect();
    const dpr = Math.max(1, window.devicePixelRatio || 1);
    const w = Math.max(1, Math.floor(r.width * dpr));
    const h = Math.max(1, Math.floor(r.height * dpr));
    if (cvs.width !== w || cvs.height !== h) {
      cvs.width = w;
      cvs.height = h;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }
  }

  const ro = new ResizeObserver(placeOverlay);
  ro.observe(host);
  window.addEventListener("resize", placeOverlay, { passive: true });
  window.addEventListener("scroll", placeOverlay, { passive: true });
  placeOverlay();

  const isGlobalId = (id) => {
    const s = String(id || "").toLowerCase();
    return s === "global" || s === "ambient" || s === "globalambient" || s.includes("global");
  };
  const presetFromLS = () => {
    try { return localStorage.getItem(UI_TARGETING_PRESET_KEY) || "originDirection"; }
    catch { return "originDirection"; }
  };

  let needsDraw = true;
  function onDirty() { needsDraw = true; }

  let inputEnabled = false;
  let lastPointer = null;

  let lastOverlayAngle = null;
  let lastOverlayEmitT = 0;
  const OVERLAY_MIN_DT_MS = 80;
  const OVERLAY_EPS_DEG = 0.8;

  function emitOverlayAngleFromLastPointer() {
    if (!inputEnabled || !lastPointer) return;
    const now = (performance && performance.now) ? performance.now() : Date.now();
    if (now - lastOverlayEmitT < OVERLAY_MIN_DT_MS) return;

    const rect = host.getBoundingClientRect();
    const mx = lastPointer.clientX - rect.left;
    const my = lastPointer.clientY - rect.top;
    const origin = getOrigin() || { x: 0, y: 0 };
    const dx = mx - origin.x;
    const dy = my - origin.y;
    const angleDeg = ((Math.atan2(dy, dx) * 180) / Math.PI + 360) % 360;

    if (lastOverlayAngle != null) {
      const diff = Math.abs(angleDeg - lastOverlayAngle);
      if (diff < OVERLAY_EPS_DEG) return;
    }

    lastOverlayAngle = angleDeg;
    lastOverlayEmitT = now;

    try {
      window.dispatchEvent(new CustomEvent("EmitterTool:UI:EmitterOverlayConfig", {
        detail: { show: true, settings: { centerLocked: true, angleDeg, rotationDeg: angleDeg } }
      }));
    } catch {}
  }

  let currentRule = null;
  let currentPresetId = null;
  let installing = false;

  function destroyRule() {
    try { currentRule?.destroy?.(); } catch {}
    currentRule = null;
  }

  function installRuleForPreset(idRaw) {
    if (!idRaw && !currentPresetId) return;
    const id = String(idRaw || currentPresetId || "").toLowerCase();
    if (!id) return;
    if (id === currentPresetId && currentRule) { needsDraw = true; return; }
    if (installing) return;
    installing = true;

    destroyRule();
    currentPresetId = id;
    cvs.style.visibility = "visible";

    if (isGlobalId(id)) {
      inputEnabled = false;
      try { window.__ET_INPUT_DISABLED = true; } catch {}
      try {
        ctx.save();
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.clearRect(0, 0, cvs.width, cvs.height);
        ctx.restore();
      } catch {}
      currentRule = installGlobalAmbient({
        canvas: cvs, ctx, getOrigin, onDirty,
        dispatch: (n, d) => { try { window.dispatchEvent(new CustomEvent(n, { detail: d })); } catch {} }
      });
      needsDraw = true;
      installing = false;
      return;
    }

    inputEnabled = true;
    try { window.__ET_INPUT_DISABLED = false; } catch {}

    const env = {
      canvas: cvs,
      ctx,
      getOrigin,
      onDirty,
      dispatch: (name, detail) => {
        try { window.dispatchEvent(new CustomEvent(name, { detail })); } catch {}
      }
    };

    if (id === "point" || id === "pointtotarget") {
      currentRule = installPointToTarget(env);
    } else if (id === "tokentotarget") {
      currentRule = installTokenToTarget(env);
    } else {
      currentRule = installOriginDirection(env);
    }

    currentRule?.pickStart?.("location");
    needsDraw = true;
    installing = false;
  }

  let pendingPresetId = null;
  let presetRaf = 0;
  function scheduleInstall(id) {
    if (!id) return;
    const low = String(id).toLowerCase();
    if (low === currentPresetId) return;
    pendingPresetId = low;
    if (presetRaf) return;
    presetRaf = requestAnimationFrame(() => {
      presetRaf = 0;
      const next = pendingPresetId;
      pendingPresetId = null;
      if (next && next !== currentPresetId) installRuleForPreset(next);
    });
  }

  function onPresetEvt(e) {
    const id = e?.detail?.presetId;
    if (typeof id !== "string") return;
    scheduleInstall(id);
  }

  function onModeChange() {
    if (inputEnabled) currentRule?.pickStart?.("location");
  }

  function isInSidebarTarget(t) {
    try {
      if (!t) return false;
      if (typeof t.closest !== "function") return false;
      return !!t.closest('[data-et-ui="sidebar"]');
    } catch { return false; }
  }

  function onPointerMove(e)  { if (!inputEnabled) return; lastPointer = e; currentRule?.onPointerMove?.(e);  needsDraw = true; }
  function onPointerEnter(e) { if (!inputEnabled) return; lastPointer = e; currentRule?.onPointerEnter?.(e); needsDraw = true; }
  function onPointerLeave(e) { if (!inputEnabled) return; currentRule?.onPointerLeave?.(e); needsDraw = true; }
  function onClick(e)        { if (!inputEnabled) return; if (isInSidebarTarget(e.target)) return; currentRule?.onClick?.(e); needsDraw = true; }

  host.addEventListener("pointermove", onPointerMove,  { passive: true });
  host.addEventListener("pointerenter", onPointerEnter, { passive: true });
  host.addEventListener("pointerleave", onPointerLeave, { passive: true });
  host.addEventListener("click",        onClick);

  function onWinMove(e){ onPointerMove(e); }
  function onWinClick(e){ if (isInSidebarTarget(e.target)) return; onClick(e); }

  window.addEventListener("pointermove", onWinMove, { passive: true, capture: true });
  window.addEventListener("click", onWinClick, { capture: true });

  let rafId = 0;
  function loop() {
    rafId = requestAnimationFrame(loop);
    placeOverlay();
    if (!needsDraw) {
      emitOverlayAngleFromLastPointer();
      return;
    }
    needsDraw = false;
    try {
      ctx.save();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, cvs.width, cvs.height);
      ctx.restore();
    } catch {}
    try { currentRule?.draw?.(ctx); } catch {}
    emitOverlayAngleFromLastPointer();
  }
  loop();

  installRuleForPreset(presetFromLS());

  window.addEventListener("EmitterTool:UI:TargetingPreset", onPresetEvt);
  window.addEventListener("EmitterTool:ModeChange", onModeChange);

  return {
    destroy() {
      try { ro.disconnect(); } catch {}
      try { mo?.disconnect?.(); } catch {}
      window.removeEventListener("resize", placeOverlay);
      window.removeEventListener("scroll", placeOverlay);
      window.removeEventListener("EmitterTool:UI:TargetingPreset", onPresetEvt);
      window.removeEventListener("EmitterTool:ModeChange", onModeChange);
      host.removeEventListener("pointermove", onPointerMove,  { passive: true });
      host.removeEventListener("pointerenter", onPointerEnter, { passive: true });
      host.removeEventListener("pointerleave", onPointerLeave, { passive: true });
      host.removeEventListener("click",        onClick);
      window.removeEventListener("pointermove", onWinMove, { capture: true });
      window.removeEventListener("click", onWinClick, { capture: true });
      if (presetRaf) cancelAnimationFrame(presetRaf);
      if (rafId) cancelAnimationFrame(rafId);
      try { currentRule?.destroy?.(); } catch {}
      try { host.removeChild(cvs); } catch {}
      if (!prevPos) host.style.removeProperty("position");
      else host.style.position = prevPos;
      try { window.__ET_INPUT_DISABLED = false; } catch {}
    }
  };
}
